import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import partialRight from 'lodash/partialRight';
import { useUniqueId } from '@wix/da-hooks/pkg/useUniqueId';
import { getEstimatedEarnings, getServiceFeePercent, getIsDisplayInitialized, getIsReduxLoaded, getRequestInfo, getActualPrice } from './redux/selectors';
import { destroyEarningsDisplay, initializeEarningsDisplay, updatePrice } from './redux/actions';
function useDisplaySpecificSelector(displayId, selector) {
    const displaySpecificSelector = partialRight(selector, displayId);
    return useSelector(displaySpecificSelector);
}
function useInitializationEffect(displayId, productType, isResale, price) {
    const dispatch = useDispatch();
    const isReduxLoaded = useSelector(getIsReduxLoaded);
    const isDisplayInitialized = useDisplaySpecificSelector(displayId, getIsDisplayInitialized);
    useEffect(()=>{
        if (isReduxLoaded && !isDisplayInitialized) {
            dispatch(initializeEarningsDisplay(displayId, productType, isResale, price));
        }
    }, [
        dispatch,
        displayId,
        productType,
        isReduxLoaded,
        price,
        isDisplayInitialized,
        isResale
    ]);
}
function useDebouncedPriceUpdateEffect(displayId, price) {
    const dispatch = useDispatch();
    const isReduxLoaded = useSelector(getIsReduxLoaded);
    const isDisplayInitialized = useDisplaySpecificSelector(displayId, getIsDisplayInitialized);
    const priceStoredInRedux = useDisplaySpecificSelector(displayId, getActualPrice);
    const dispatchUpdatePrice = useCallback((storedPrice, incomingPrice)=>{
        if (isReduxLoaded && isDisplayInitialized && // When we initialize the display, we also fire a request to the backend.
        // This check makes sure we don't double up on that request.
        storedPrice !== incomingPrice) {
            dispatch(updatePrice(displayId, incomingPrice));
        }
    }, [
        dispatch,
        displayId,
        isReduxLoaded,
        isDisplayInitialized
    ]);
    const debouncedUpdatePrice = useMemo(()=>debounce(dispatchUpdatePrice, 500), [
        dispatchUpdatePrice
    ]);
    useEffect(()=>{
        debouncedUpdatePrice(priceStoredInRedux, price);
    }, [
        debouncedUpdatePrice,
        price,
        priceStoredInRedux
    ]);
}
function useCleanupEffect(displayId) {
    const dispatch = useDispatch();
    useEffect(()=>{
        return ()=>{
            dispatch(destroyEarningsDisplay(displayId));
        };
    }, [
        dispatch,
        displayId
    ]);
}
export function useEstimatedEarningsDisplay(price, productType, isResale = false) {
    const displayId = useUniqueId();
    useInitializationEffect(displayId, productType, isResale, price);
    useDebouncedPriceUpdateEffect(displayId, price);
    useCleanupEffect(displayId);
    const earnings = useDisplaySpecificSelector(displayId, getEstimatedEarnings);
    const serviceFeePercent = useDisplaySpecificSelector(displayId, getServiceFeePercent);
    const requestInfo = useDisplaySpecificSelector(displayId, getRequestInfo);
    return {
        displayId,
        earnings,
        serviceFeePercent,
        requestInfo
    };
}
